import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import type { AxiosError } from "axios";
import {
	Box,
	Typography,
	Button,
	Paper,
	Grid,
	RadioGroup,
	Radio,
	FormControl,
	Alert,
	CircularProgress,
	Divider,
	FormControlLabel,
	Checkbox,
	Select,
	MenuItem,
	InputLabel,
} from "@mui/material";
import type {
	ELearningResponse,
	UserAnswer,
	ELearningSubmission,
	ELearningResult,
} from "@/models/eLearning";
import { ELearningConstants } from "./ELearningConstants";
import {
	getELearningAPI,
	submitELearningAPI,
	getPresignedUrlAPI,
} from "@/util/api/eLearning";

/**
 * 合格メッセージブロック
 * PassedMessage と PassedMessageNote を表示するコンポーネント
 */
const PassedMessageBlock = () => (
	<Box>
		<Typography variant="body2">{ELearningConstants.PassedMessage}</Typography>
		<Typography variant="body2" sx={{ mt: 1 }}>
			{ELearningConstants.PassedMessageCloseNote}
		</Typography>
	</Box>
);

export const ELearningPage = () => {
	const { id } = useParams<{ id: string }>();

	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [data, setData] = useState<ELearningResponse | null>(null);
	const [answers, setAnswers] = useState<UserAnswer[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [submitted, setSubmitted] = useState(false);
	const [selectedResponsiblePerson, setSelectedResponsiblePerson] = useState<{
		personId: number;
	} | null>(null);
	const [materialConfirmed, setMaterialConfirmed] = useState(false);
	const [nameConfirmed, setNameConfirmed] = useState(false);
	const [testResult, setTestResult] = useState<ELearningResult | null>(null);

	// 現在選択された担当者の割り当て情報
	const selectedAssignment = useMemo(() => {
		return data?.assignments?.find(
			(assignment) =>
				assignment.personId === selectedResponsiblePerson?.personId,
		);
	}, [data?.assignments, selectedResponsiblePerson]);

	// 利用可能な学習者情報一覧（氏名・営業所名・メールアドレス）
	const availableLearners = useMemo(() => {
		return (
			data?.assignments?.map((assignment) => ({
				personId: assignment.personId,
				name: assignment.name,
				emailAddress: assignment.emailAddress,
				officeName: assignment.officeName,
				isPassed: assignment.isPassed,
				label: `${assignment.name}（${assignment.officeName}）/ ${assignment.emailAddress}`,
			})) || []
		);
	}, [data?.assignments]);

	// 合格済みかどうか（testResult の isPassed、または assignments の isPassed から判定）
	const isPassedDerived = useMemo(() => {
		// testResult の合否判定を優先（最新のテスト結果）
		if (testResult?.isPassed !== undefined) {
			return testResult.isPassed;
		}
		// assignments の合否判定をフォールバック
		return selectedAssignment?.isPassed || false;
	}, [testResult?.isPassed, selectedAssignment?.isPassed]);

	// 基本的なUI状態
	const isResponsiblePersonSelected = useMemo(() => {
		return !!selectedResponsiblePerson?.personId;
	}, [selectedResponsiblePerson]);

	const hasMaterialFiles = useMemo(() => {
		return data?.materialFileNames && data.materialFileNames.length > 0;
	}, [data?.materialFileNames]);

	const isMaterialConfirmed = useMemo(() => {
		return materialConfirmed;
	}, [materialConfirmed]);

	// 複合的なUI条件
	const isMaterialConfirmationRequired = useMemo(() => {
		return hasMaterialFiles && !isMaterialConfirmed;
	}, [hasMaterialFiles, isMaterialConfirmed]);

	const canShowQuestions = useMemo(() => {
		return !hasMaterialFiles || isMaterialConfirmed;
	}, [hasMaterialFiles, isMaterialConfirmed]);

	const shouldShowMaterialSection = useMemo(() => {
		return hasMaterialFiles && isResponsiblePersonSelected;
	}, [hasMaterialFiles, isResponsiblePersonSelected]);

	const shouldShowQuestions = useMemo(() => {
		// 初期ロード時点で合格していたら非表示
		const initiallyPassed = selectedAssignment?.isPassed && !submitted;
		// それ以外（提出後など）は常に表示
		return (
			!initiallyPassed &&
			isResponsiblePersonSelected &&
			canShowQuestions &&
			nameConfirmed
		);
	}, [
		selectedAssignment?.isPassed,
		submitted,
		isResponsiblePersonSelected,
		canShowQuestions,
		nameConfirmed,
	]);

	const canSubmitAnswers = useMemo(() => {
		return (
			isResponsiblePersonSelected &&
			!isMaterialConfirmationRequired &&
			nameConfirmed
		);
	}, [
		isResponsiblePersonSelected,
		isMaterialConfirmationRequired,
		nameConfirmed,
	]);

	// エラー管理の整理
	const networkOrValidationError = useMemo(() => {
		return error; // Network/UI validation errors
	}, [error]);

	const testResultError = useMemo(() => {
		return testResult?.errorMessage || null; // Business logic errors from test submission
	}, [testResult?.errorMessage]);

	const hasSuccessfulTestResult = useMemo(() => {
		return submitted && testResult && !testResultError;
	}, [submitted, testResult, testResultError]);

	// 公開期間チェック
	const isAccessible = (): boolean => {
		if (!data) return false;
		const now = new Date();
		const startDate = new Date(data.publishedOn);
		// unpublishedAtがnullの場合は常にアクセス可能
		if (!data.unpublishedOn) return now >= startDate;
		const endDate = new Date(data.unpublishedOn);
		endDate.setHours(23, 59, 59, 999);
		return now >= startDate && now <= endDate;
	};

	// データ取得関数（初回取得と再取得で共通使用）
	const fetchELearningData = useCallback(
		async (showLoading = true) => {
			if (!id) {
				setError("eラーニングIDが指定されていません。");
				if (showLoading) setLoading(false);
				return;
			}

			if (showLoading) setLoading(true);

			try {
				// eラーニングデータを取得
				const response = await getELearningAPI(id);
				const apiData = response.data;
				setData(apiData);

				if (apiData?.questions && showLoading) {
					// 初回のみ回答を初期化
					const initialAnswers = apiData.questions.map((q) => ({
						questionId: q.id,
						selectedAnswer: null,
					}));
					setAnswers(initialAnswers);
				}
			} catch {
				setError("データの取得に失敗しました。");
			} finally {
				if (showLoading) setLoading(false);
			}
		},
		[id],
	);

	// 初回データ取得
	useEffect(() => {
		fetchELearningData();
	}, [fetchELearningData]);

	// 回答変更ハンドラ
	const handleAnswerChange = (questionId: number, choiceId: number) => {
		setAnswers((prev) =>
			prev.map((answer) =>
				answer.questionId === questionId
					? { ...answer, selectedAnswer: choiceId }
					: answer,
			),
		);
	};

	// 回答提出
	const handleSubmit = async () => {
		if (!data) return;

		// バリデーション
		if (!selectedResponsiblePerson?.personId) {
			setError(ELearningConstants.ResponsiblePersonSelectionMessage);
			return;
		}

		// 画面の並び（責任者選択 → 本人確認 → 教材確認 → 設問）の順に検証する
		if (!nameConfirmed) {
			setError(ELearningConstants.IdentityConfirmationRequired);
			return;
		}

		// 教材ファイルが存在する場合は教材確認が必要
		if (isMaterialConfirmationRequired) {
			setError(ELearningConstants.MaterialConfirmationRequired);
			return;
		}

		const unansweredQuestions = answers.filter(
			(answer) =>
				answer.selectedAnswer === null || answer.selectedAnswer === undefined,
		);
		if (unansweredQuestions.length > 0) {
			setError(ELearningConstants.AllQuestionsRequired);
			return;
		}

		setSubmitting(true);
		setError(null);

		try {
			// 実際のAPI呼び出し
			const submission: ELearningSubmission = {
				programId: data.id,
				answers: answers,
				personId: selectedResponsiblePerson.personId,
				nameConfirmed,
			};
			const result = await submitELearningAPI(submission);

			if (result.data) {
				setTestResult(result.data);
			}

			setSubmitted(true);

			// 提出後に最新のデータを再取得（合格状況を更新）
			await fetchELearningData(false);
		} catch {
			setError("回答の提出に失敗しました。");
		} finally {
			setSubmitting(false);
		}
	};

	// 再挑戦
	const handleRetry = () => {
		setSubmitted(false);
		setTestResult(null);
		setAnswers((prev) =>
			prev.map((answer) => ({ ...answer, selectedAnswer: null })),
		);
		setError(null);
	};

	const downloadMaterial = useCallback(
		(fileName: string) => {
			if (!data?.id) {
				setError("eラーニング情報が不正です。");
				return;
			}

			getPresignedUrlAPI(data.id.toString(), fileName)
				.then(({ data: url }) => {
					const anchor = document.createElement("a");
					anchor.href = url;
					anchor.target = "_blank";
					anchor.rel = "noopener";
					anchor.download = fileName;
					document.body.appendChild(anchor);
					anchor.click();
					document.body.removeChild(anchor);
				})
				.catch((e: AxiosError) => {
					setError(`教材ファイルのダウンロードに失敗しました: ${e.message}`);
				});
		},
		[data?.id],
	);

	if (loading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="400px"
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!data || !data.id) {
		return (
			<Box p={3}>
				<Alert severity="error">{error || "データが見つかりません。"}</Alert>
			</Box>
		);
	}

	if (!isAccessible()) {
		return (
			<Box p={3}>
				<Alert severity="warning">{ELearningConstants.NotAccessible}</Alert>
			</Box>
		);
	}

	if (availableLearners.length === 0) {
		// 氏名を確認できず受講者から除外された場合は、未回答とは原因が異なるため案内を分ける
		return (
			<Box p={3}>
				<Alert severity="warning">
					{data.hasLearnerWithoutName
						? ELearningConstants.LearnerNameUnavailable
						: ELearningConstants.NotEligible}
				</Alert>
			</Box>
		);
	}

	return (
		<Box p={3}>
			<Paper sx={{ p: 3, mb: 3 }}>
				<Typography variant="h4" gutterBottom>
					{data.title}
				</Typography>

				{data.description && (
					<Typography
						variant="body1"
						sx={{
							whiteSpace: "pre-wrap",
							mb: 2,
							color: "text.primary",
						}}
					>
						{data.description}
					</Typography>
				)}
			</Paper>

			{/* 担当者選択セクション */}
			<Paper sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					{ELearningConstants.ResponsiblePersonSelection}
				</Typography>
				<Typography variant="body2" color="textSecondary" paragraph>
					{ELearningConstants.ResponsiblePersonSelectionMessage}
					<br />
					{ELearningConstants.ResponsiblePersonSelectionNote}
				</Typography>
				<Typography
					variant="caption"
					color="textSecondary"
					sx={{ mb: 2, display: "block" }}
				></Typography>
				<FormControl fullWidth sx={{ mb: 2 }}>
					<InputLabel id="responsible-person-select-label">
						{ELearningConstants.Learner}
					</InputLabel>
					<Select
						labelId="responsible-person-select-label"
						value={
							selectedResponsiblePerson
								? selectedResponsiblePerson.personId
								: ""
						}
						label={ELearningConstants.Learner}
						onChange={(e) => {
							const selectedValue = e.target.value as number;
							if (!selectedValue) {
								setSelectedResponsiblePerson(null);
								return;
							}

							setSelectedResponsiblePerson({
								personId: selectedValue as number,
							});

							// メールアドレス切り替え時にテスト状態をクリア
							setSubmitted(false);
							setTestResult(null);
							setError(null);
							// 回答もクリア
							setAnswers((prev) =>
								prev.map((answer) => ({ ...answer, selectedAnswer: null })),
							);

							// 教材確認・本人確認状態もリセット
							setMaterialConfirmed(false);
							setNameConfirmed(false);
						}}
					>
						{/* 表示順は API 側で確定しているため、返却順のまま描画する */}
						{availableLearners.map((learner) => (
							<MenuItem key={learner.personId} value={learner.personId}>
								{`${learner.isPassed ? "【合格済み】" : ""}${learner.label}`}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Paper>

			{/* 本人確認セクション */}
			{!submitted && selectedAssignment && !selectedAssignment.isPassed && (
				<Paper sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" gutterBottom>
						{ELearningConstants.IdentityConfirmationTitle}
					</Typography>
					<Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
						{ELearningConstants.IdentityConfirmationStatement(
							selectedAssignment.name,
							selectedAssignment.officeName,
						)}
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								checked={nameConfirmed}
								onChange={(e) => setNameConfirmed(e.target.checked)}
								color="primary"
							/>
						}
						label={
							<Typography variant="body2">
								{ELearningConstants.IdentityConfirmation}
							</Typography>
						}
					/>
				</Paper>
			)}

			{/* 教材確認セクション */}
			{shouldShowMaterialSection && (
				<Paper sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" gutterBottom>
						{ELearningConstants.ConfirmMaterials}
					</Typography>
					<Box sx={{ mb: 2 }}>
						<Typography variant="caption" color="textSecondary" display="block">
							{ELearningConstants.MaterialSectionNote1}
							<br />
							{ELearningConstants.MaterialSectionNote2}
						</Typography>
					</Box>
					<Grid container spacing={1} sx={{ mb: 2 }}>
						{data.materialFileNames?.map((material) => (
							<Grid item key={material}>
								<Button
									variant="outlined"
									size="small"
									onClick={() => downloadMaterial(material)}
								>
									{material}
								</Button>
							</Grid>
						))}
					</Grid>

					{/* 教材確認チェックボックス */}
					{isPassedDerived ? null : (
						<Box
							sx={{
								mt: 2,
								p: 2,
								backgroundColor: "#fff3e0",
								borderRadius: 1,
								border: "1px solid #ffcc02",
							}}
						>
							<Typography
								variant="body2"
								sx={{ mb: 1, fontWeight: "bold", color: "#e65100" }}
							>
								{ELearningConstants.MaterialSectionWarningTitle}
							</Typography>
							<Typography variant="body2" sx={{ mb: 2, color: "#bf360c" }}>
								{ELearningConstants.MaterialSectionWarningBody1}
								<br />
								{ELearningConstants.MaterialSectionWarningBody2}
								<br />
								{ELearningConstants.MaterialSectionWarningBody3}
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										checked={materialConfirmed}
										onChange={(e) => setMaterialConfirmed(e.target.checked)}
										color="primary"
									/>
								}
								label={
									<Typography variant="body2" sx={{ fontWeight: "bold" }}>
										{ELearningConstants.MaterialConfirmation}
									</Typography>
								}
							/>
						</Box>
					)}
				</Paper>
			)}

			{/* 合格済み表示 */}
			{isPassedDerived && (
				<Alert severity="success" sx={{ mb: 3 }}>
					<Typography variant="h6">{ELearningConstants.Passed}</Typography>
					<PassedMessageBlock />
				</Alert>
			)}

			{shouldShowQuestions && (
				<>
					{/* 設問セクション */}
					<Paper sx={{ p: 3, mb: 3 }}>
						<Typography variant="h6" gutterBottom>
							{ELearningConstants.Questions}
						</Typography>

						{networkOrValidationError && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{networkOrValidationError}
							</Alert>
						)}

						{data.questions.map((question, questionIndex) => {
							const isIncorrect =
								hasSuccessfulTestResult &&
								testResult?.incorrectQuestionIds?.some(
									(id) => id === question.id,
								);
							const isCorrect = hasSuccessfulTestResult && !isIncorrect;

							return (
								<Box
									key={question.id}
									mb={3}
									sx={{
										border: hasSuccessfulTestResult
											? isIncorrect
												? "2px solid #f44336"
												: isCorrect
													? "2px solid #4caf50"
													: "none"
											: "none",
										borderRadius: 1,
										p: hasSuccessfulTestResult ? 2 : 0,
										backgroundColor: hasSuccessfulTestResult
											? isIncorrect
												? "#ffebee"
												: isCorrect
													? "#e8f5e8"
													: "transparent"
											: "transparent",
									}}
								>
									<Box display="flex" alignItems="center" gap={1}>
										<Box mb={1} width="100%">
											<Typography variant="h6" component="div">
												問{questionIndex + 1}.
											</Typography>
											<Typography
												variant="body1"
												sx={{ whiteSpace: "pre-wrap", ml: 2 }}
											>
												{question.questionText}
											</Typography>
										</Box>
										{hasSuccessfulTestResult && isIncorrect && (
											<Typography
												variant="body2"
												sx={{
													display: "inline-block",
													minWidth: 60,
													fontWeight: "bold",
													backgroundColor: "#f44336",
													color: "white",
													px: 1,
													py: 0.5,
													borderRadius: 1,
													fontSize: "0.75rem",
													textAlign: "center",
												}}
											>
												不正解
											</Typography>
										)}
										{hasSuccessfulTestResult && isCorrect && (
											<Typography
												variant="body2"
												sx={{
													display: "inline-block",
													minWidth: 60,
													fontWeight: "bold",
													backgroundColor: "#4caf50",
													color: "white",
													px: 1,
													py: 0.5,
													borderRadius: 1,
													fontSize: "0.75rem",
													textAlign: "center",
												}}
											>
												正解
											</Typography>
										)}
									</Box>

									<FormControl component="fieldset" disabled={isPassedDerived}>
										<RadioGroup
											value={
												answers.find((a) => a.questionId === question.id)
													?.selectedAnswer || null
											}
											onChange={(e) =>
												handleAnswerChange(
													question.id,
													parseInt(e.target.value, 10),
												)
											}
										>
											{question.options.map((choice) => (
												<FormControlLabel
													key={choice.id}
													value={choice.id}
													control={<Radio />}
													label={choice.text}
												/>
											))}
										</RadioGroup>

										{testResult?.questionExplanations?.some(
											(q) => q.id === question.id,
										) && (
											<Box
												sx={{
													ml: 2,
													mt: 2,
													p: 2,
													backgroundColor: "#f5f5f5", // 常にグレーの背景
													borderRadius: 1,
													border: "1px solid #e0e0e0",
												}}
											>
												<Typography
													variant="body2"
													sx={{
														fontWeight: "bold",
														color: "#1976d2", // 常に青（情報）
														mb: 1,
													}}
												>
													{ELearningConstants.Explanation}：
												</Typography>
												<Typography
													variant="body2"
													sx={{ color: "#424242", whiteSpace: "pre-wrap" }}
												>
													{
														testResult.questionExplanations.find(
															(q) => q.id === question.id,
														)?.explanation
													}
												</Typography>
											</Box>
										)}
									</FormControl>

									{/* 不正解の設問に解説を表示 */}
									{hasSuccessfulTestResult &&
										isIncorrect &&
										question.explanation && (
											<Box
												sx={{
													mt: 2,
													p: 2,
													backgroundColor: "#fff3e0",
													borderRadius: 1,
													border: "1px solid #ff9800",
												}}
											>
												<Typography
													variant="body2"
													sx={{ fontWeight: "bold", color: "#e65100", mb: 1 }}
												>
													💡 {ELearningConstants.Explanation}
												</Typography>
												<Typography variant="body2" sx={{ color: "#bf360c" }}>
													{question.explanation}
												</Typography>
											</Box>
										)}

									{questionIndex < data.questions.length - 1 && (
										<Divider sx={{ mt: 2 }} />
									)}
								</Box>
							);
						})}
					</Paper>

					{/* 結果表示 */}
					{submitted && (
						<Alert
							severity={
								testResultError
									? "warning"
									: isPassedDerived
										? "success"
										: "error"
							}
							sx={{ mb: 3 }}
						>
							<Typography variant="body1" component="div">
								{testResultError ? (
									testResultError
								) : isPassedDerived ? (
									<PassedMessageBlock />
								) : (
									ELearningConstants.FailedMessage
								)}
							</Typography>

							{/* 正解数の表示 */}
							{hasSuccessfulTestResult && (
								<Typography variant="body2" sx={{ mt: 1 }}>
									正解数: {testResult?.correctCount || 0} /{" "}
									{testResult?.totalQuestions || data.questions.length}
								</Typography>
							)}
						</Alert>
					)}

					{/* アクションボタン */}
					<Box display="flex" gap={2}>
						{!submitted && (
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmit}
								disabled={submitting || !canSubmitAnswers}
							>
								{submitting ? (
									<CircularProgress size={24} />
								) : (
									ELearningConstants.Submit
								)}
							</Button>
						)}

						{submitted && !isPassedDerived && !testResultError && (
							<Button variant="contained" color="primary" onClick={handleRetry}>
								{ELearningConstants.Retry}
							</Button>
						)}
					</Box>
				</>
			)}
		</Box>
	);
};

export default ELearningPage;
